import Immutable from 'immutable';

import FilterRule from 'sharedreact/data/FilterRule';

function _stringToFilter (filterString: string | null, supportedKeys: Immutable.List<string> | string[], parse: boolean, allowWildCard: boolean): {rules: Immutable.List<FilterRule>} {
  const FilterOperator: {
    AND: string,
    OR: string
  } = {
    AND: 'AND',
    OR: 'OR'
  };

  if (!filterString) {
    return {rules: Immutable.List()};
  }

  let filterRules: Immutable.List<FilterRule> = Immutable.List();

  let operatorValid: boolean = true;
  let operator: string = '';
  let stringData: string = '';
  let key: string | null = null;
  let ruleOperator: string = FilterRule.CONTAINS;

  if (!parse) {
    filterRules = filterRules.push(FilterRule.createRule(null, filterString));
  } else {
    let isQuoted: boolean = false;

    // remove whitespaces from both ends of the string before parsing
    filterString = filterString.trim();

    for (let charIndex = 0; charIndex < filterString.length; charIndex++) {
      const char: string = filterString.charAt(charIndex);

      if (char === '"') {
        isQuoted = !isQuoted;
      } else if (char === ' ' && !isQuoted) {
        if (stringData.length > 0) {
          if (operatorValid && (operator === '' || operator === FilterOperator.AND)) {
            operator = FilterOperator.AND;

            filterRules = filterRules.push(FilterRule.createRule(key, stringData, ruleOperator));
            ruleOperator = FilterRule.CONTAINS;
            if (filterRules.size > 1) {
              filterRules = filterRules.push(FilterRule.createAND());
            }
          } else {
            operatorValid = false;
            operator = '';
          }

          stringData = '';
          key = null;
        }
      } else if (char === ':' && !isQuoted) {
        const possibleKey: string = FilterRule.stringToKey(stringData);
        if (FilterRule.isSupportedKey(possibleKey, supportedKeys)) {
          key = possibleKey;
          stringData = '';
        } else {
          stringData += char;
        }
      } else if (char === '=' && !isQuoted && stringData.trim().length === 0) {
        ruleOperator = FilterRule.IS;
      } else if (allowWildCard && char === '*' && !isQuoted) {
        // currently only we allow "starts with" and "ends with"
        if (stringData.trim().length === 0) {
          ruleOperator = FilterRule.ENDS_WITH;
        } else if (charIndex + 1 >= filterString.length || filterString.charAt(charIndex + 1) === ' ') {
          if (ruleOperator === FilterRule.ENDS_WITH) {
            ruleOperator = FilterRule.CONTAINS;
          } else {
            ruleOperator = FilterRule.STARTS_WITH;
          }
        } else {
          stringData += char;
        }
      } else {
        stringData += char;
      }
    }
  }

  if (stringData.length > 0) {
    filterRules = filterRules.push(FilterRule.createRule(key, stringData, ruleOperator));
  } else if (key && key.length > 0) {
    filterRules = filterRules.push(FilterRule.createRule(null, key + ':', ruleOperator));
  }

  if (operatorValid && operator === '') {
    operator = FilterOperator.AND;
  }

  if (filterRules.size > 1) {
    if (operator === FilterOperator.AND) {
      filterRules = filterRules.push(FilterRule.createAND());
    } else if (operator === FilterOperator.OR) {
      filterRules = filterRules.push(FilterRule.createOR());
    }
  }

  return {rules: filterRules};
}

function _filterToString (rules: Immutable.List<FilterRule>): string {
  let useAND: boolean = true;
  const actualFilterRules: Immutable.List<FilterRule> = rules.filter((filterRule) => {
    if (FilterRule.isOR(filterRule)) {
      useAND = false;
    }
    return !FilterRule.isAND(filterRule) && !FilterRule.isOR(filterRule);
  });

  return actualFilterRules.map((filterRule) => {
    return filterRule.toString();
  }).join(useAND ? ' ' : ';');
}

const Content: {
  rules: Immutable.List<FilterRule>
} = {
  rules: Immutable.List()
};

class Filter extends Immutable.Record(Content) {
  toString (): string {
    return _filterToString(this.rules);
  }

  addRule (key: string | null, value: string, operator?: string): Filter {
    return this.set('rules', this.rules.push(FilterRule.createRule(key ? FilterRule.stringToKey(key) : null, value, operator)));
  }

  addAND (): Filter {
    return this.set('rules', this.rules.push(FilterRule.createAND()));
  }

  addOR (): Filter {
    return this.set('rules', this.rules.push(FilterRule.createOR()));
  }

  static createFromString (filterString: string | null, supportedKeys: Immutable.List<string> | string[], parse: boolean = true, allowWildCard: boolean = false): Filter {
    return new Filter(_stringToFilter(filterString, supportedKeys, parse, allowWildCard));
  }

  static createFromRules (rules: FilterRule[]): Filter {
    return new Filter({rules: Immutable.List(rules)});
  }

  static createFromRuleData (key: string | null, value: string, operator?: string): Filter {
    return Filter.createEmpty().addRule(key, value, operator);
  }

  static createEmpty (): Filter {
    return new Filter({rules: Immutable.List()});
  }

  hasLabels (): boolean {
    return (this.rules.findIndex((rule) => rule.key != null) !== -1);
  }

  removeUnsupportedRules (supportedfilterKeys: string[]): Filter {
    // remove rules with unsupported filter key and any operators for those removed rules
    let newFilterRules: FilterRule[] = [];
  
    let operands: (FilterRule | null)[] = [];
  
    for (const filterRule of this.rules) {
      if (FilterRule.isAND(filterRule) || FilterRule.isOR(filterRule)) {
        const lastOperand = operands.pop();
        const firstOperand = operands.pop();
  
        if (firstOperand != null && lastOperand != null) {
          newFilterRules.push(filterRule);
        }
      } else if (filterRule.key == null || supportedfilterKeys.findIndex((supportedKey) => filterRule.key === supportedKey) !== -1) {
        newFilterRules.push(filterRule);
        operands.push(filterRule);
      } else {
        // unsupported rule, so set operand to null, so we can remove the AND or OR that uses this operand
        operands.push(null);
      }
    }
  
    const supportedRules: Immutable.List<FilterRule> = Immutable.List(newFilterRules);
  
    return this.set('rules', supportedRules);
  }
}

export default Filter;
